import React, { useState, useMemo, useEffect } from 'react';
import { DatePicker, Button, Typography, notification } from 'antd';
import { useQuery } from 'react-query';
import { CloudDownloadOutlined } from '@ant-design/icons';
import { useLocation } from 'react-router-dom';

import {
  BatchJobExecution,
  getJobListByCriteriaClientInvoice,
  GetJobListByCriteriaParams,
} from 'services/clientInvoice';
import DF from 'DisplayFramework';
import DataGrid from 'components/DataGrid/DataGrid';
import ParListSelect from 'components/ParListSelect';
import { DataGridColDef } from 'components/DataGrid/DataGrid';
import ServiceSelect from '../Components/ServiceSelect';
import ExportCIListButton from '../Components/ExportCIListButton';
import { API_URLS } from 'utils/config';
import { downloadBlobFile } from 'utils/utils2';
import { getJobListByCriteriaPayement } from 'services/PaymentServices';
import { getJobListByCriteriaSupplierInvoice } from 'services/supplierInvoice';
import { getJobListByCriteriaAccounting } from 'services/accounting';
import { getJobListByCriteriaCollection } from 'services/collection';

export type ServicesProps = {
  onSelect?: (job: BatchJobExecution) => void;
  moduleProps?: string;
};

const { Text } = Typography;

const getUrl = (
  jobid: number | string,
  filetype: string,
  entity: string,
  mode?: string
): string => {
  if (mode === 'PRINTING') {
    return `${API_URLS.PRINTING}/api/job/download?jobid=${jobid}&filetype=${filetype}`;
  } else if (entity === 'PAYMENT' || mode === 'PAYMENT') {
    return `${API_URLS.PAYMENT}/api/job/download?jobid=${jobid}&filetype=${filetype}`;
  } else if (entity === 'CINVOICE') {
    return `${API_URLS.CLIENT_INVOICE}/api/job/download?jobid=${jobid}&filetype=${filetype}`;
  } else if (entity === 'SINVOICE') {
    return `${API_URLS.SUPPLIER_INVOICE}/api/supplier/job/download?jobid=${jobid}&filetype=${filetype}`;
  } else if (entity === 'COLLECTION') {
    return `${API_URLS.COLLECTION}/api/collection/job/download?jobid=${jobid}&filetype=${filetype}`;
  } else {
    return `${API_URLS.ACCOUNTING}/api/job/download?jobid=${jobid}&filetype=${filetype}`;
  }
};

type ServicesFollowupProps = ServicesProps;

const ServicesFollowup = (props: ServicesFollowupProps) => {
  const { onSelect, moduleProps } = props;
  const location = useLocation();
  const search = location.search;
  const params = new URLSearchParams(search);

  const module = params.get('module') ?? '';

  const [lmodule, setLModule] = useState<string>(module || moduleProps || '');
  const [servicename, setServiceName] = useState<string>('');

  let [initialSearchParams, setInitialSearchParams] =
    useState<GetJobListByCriteriaParams>({
      module: lmodule,
      servicename: '',
      startdate: '',
      status: '',
    });

  const [searchParams, setSearchParams] =
    useState<GetJobListByCriteriaParams>(initialSearchParams);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchs, setsearchs] = useState(false);
  const [data, setdata] = useState<any[]>([]);
  const moduleFromParams = params.get('module');
  useEffect(() => {
    setdata([]);

    if (lmodule !== params.get('module')) { 
      setLModule(module);
      setServiceName('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduleFromParams]);

  const {
    data: services,
    isFetching: loading,
    refetch,
  } = useServicesFollowup(searchParams, searchs);

  useEffect(() => {
    setdata([]);
  }, [lmodule]);

  useEffect(() => {
    if (services) {
      setdata(services);
    }
  }, [services]);

  const columns = useMemo<DataGridColDef<any>[]>(
    () => [
      {
        dataIndex: 'job_execution_id',
        fixed: 'left',
        render: (value, record) =>
          onSelect ? (
            <Typography.Link
              onClick={() => {
                onSelect(record);
              }}
            >
              {value}
            </Typography.Link>
          ) : (
            value
          ),
        width: 100,
      },
      {
        dataIndex: 'module',
        fixed: 'left',
        filtrable: true,
        width: 210,
        filterComponent: (
          <ParListSelect
            parcode="EXECMODULE"
            forcedValue={lmodule}
            style={{ width: '100%' }}
          />
        ),
      },
      {
        dataIndex: 'servicename',
        fixed: 'left',
        filtrable: true,
        width: 210,
        filterComponent: (
          <ServiceSelect
            onSelect={(e) => {
              setServiceName(e);
            }}
            module={moduleProps ?? lmodule}
            style={{ width: '100%' }}
          />
        ),
      },
      {
        dataIndex: 'status',
        width: 110,
        filtrable: true,
        filterComponent: (
          <ParListSelect parcode="EXECSTAT" style={{ width: '100%' }} />
        ),
        render: (status) =>
          status === 'STARTED' ? (
            <Text type="warning">{status}</Text>
          ) : status === 'COMPLETED' ? (
            <Text style={{ color: 'green', fontWeight: 'bold' }}>{status}</Text>
          ) : (
            <Text type="danger">{status}</Text>
          ),
      },

      {
        dataIndex: 'start_time',
        width: 200,
        filtrable: 'startdate',
        filterComponent: <DatePicker format="YYYY-MM-DD" />,
        render: (date) => new Date(date).toLocaleString(),
      },
      {
        dataIndex: 'end_time',
        width: 200,
        render: (date) => (date ? new Date(date).toLocaleString() : null),
      },
      { dataIndex: 'exit_code', width: 120 },
      { dataIndex: 'serviceexecparam', width: 600 },
      {
        dataIndex: 'logfile',
        fixed: 'right',
        width: 100,
        align: 'center',
        render: (logfile, record) => {
          return (
            logfile && (
              <Button
                icon={<CloudDownloadOutlined />}
                onClick={(ev) => {
                  ev.preventDefault();
                  downloadBlobFile(
                    logfile,
                    getUrl(record.job_execution_id, 'LOG', record.module)
                  );
                }}
              />
            )
          );
        },
      },
      {
        dataIndex: 'outputfile',
        fixed: 'right',
        width: 100,
        align: 'center',
        render: (outputfile, record) => {
          return (
            outputfile && (
              <Button
                icon={<CloudDownloadOutlined />}
                onClick={(ev) => {
                  ev.preventDefault();
                  downloadBlobFile(
                    outputfile,
                    getUrl(record.job_execution_id, 'OUTPUT', record.module)
                  );
                }}
              />
            )
          );
        },
      },
      {
        dataIndex: 'errorfile',
        fixed: 'right',
        width: 190,
        align: 'center',
        render: (errorfile, record) => {
          return (
            errorfile && (
              <Button
                icon={<CloudDownloadOutlined />}
                onClick={(ev) => {
                  ev.preventDefault();
                  downloadBlobFile(
                    errorfile,
                    getUrl(record.job_execution_id, 'ERROR', record.module)
                  );
                }}
              />
            )
          );
        },
      },
      {
        dataIndex: 'jsonfile',
        fixed: 'right',
        width: 100,
        align: 'center',
        render: (job_execution_id, record) => {
          return (
            <ExportCIListButton
              setIsLoading={setIsLoading}
              jobid={record.job_execution_id}
            ></ExportCIListButton>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lmodule, searchParams, module, initialSearchParams, moduleProps]
  );

  return (
    <DF.Provider pagcode="ciservicesfollowup">
      <DataGrid
        rowKey="id"
        titleComcode="pageTitle"
        scroll={{ x: 2000, y: 400 }}
        dataSource={data}
        columns={columns}
        onSearch={(values) => {
          setSearchParams(values);
          setsearchs(true);
        }}
        onClear={() => {
          setServiceName('');
        }}
        initialSearchValues={initialSearchParams}
        isLoading={isLoading || loading}
        pagination={{ pageSize: 100 }}
      />
    </DF.Provider>
  );
};

const useServicesFollowup = (searchParams: any, searchs: boolean) => {
  const jobListByCriteriaPayement = useQuery(
    ['getJobListByCriteriaPayement', searchParams],
    () =>
      getJobListByCriteriaPayement({
        ...searchParams,
        startdate:
          typeof searchParams.startdate === 'string'
            ? searchParams.startdate
            : (searchParams.startdate as any).format('YYYY-MM-DD'),
      })
        .then((res) => res.list)
        .catch((error) => {
          notification.error({
            description: error.message,
            message: 'Asset Service is not available',
          });
          return [];
        }),
    { enabled: searchParams.module === 'PAYMENT' && searchs }
  );
  const jobListByCriteriaClientInvoice = useQuery(
    ['getJobListByCriteriaClientInvoice', searchParams],
    () =>
      getJobListByCriteriaClientInvoice({
        ...searchParams,
        startdate:
          typeof searchParams.startdate === 'string'
            ? searchParams.startdate
            : (searchParams.startdate as any).format('YYYY-MM-DD'),
      }).then((res) => res.list),
    { enabled: searchParams.module === 'CINVOICE' && searchs }
  );

  const jobListByCriteriaSupplierInvoice = useQuery(
    ['getJobListByCriteriaSupplierInvoice', searchParams],
    () =>
      getJobListByCriteriaSupplierInvoice({
        ...searchParams,
        startdate:
          typeof searchParams.startdate === 'string'
            ? searchParams.startdate
            : (searchParams.startdate as any).format('YYYY-MM-DD'),
      }).then((res) => res.list),
    { enabled: searchParams.module === 'SINVOICE' && searchs }
  );

  const jobListByCriteriaAccounting = useQuery(
    ['getJobListByCriteriaAccounting', searchParams],
    () =>
      getJobListByCriteriaAccounting({
        ...searchParams,
        startdate:
          typeof searchParams.startdate === 'string'
            ? searchParams.startdate
            : (searchParams.startdate as any).format('YYYY-MM-DD'),
      }).then((res) => res.list),
    { enabled: searchParams.module === 'ACCOUNTING' && searchs }
  );

  const jobListByCriteriaCollection = useQuery(
    ['getJobListByCriteriaCollection', searchParams],
    () =>
      getJobListByCriteriaCollection({
        ...searchParams,
        startdate:
          typeof searchParams.startdate === 'string'
            ? searchParams.startdate
            : (searchParams.startdate as any).format('YYYY-MM-DD'),
      }).then((res) => res.list),
    { enabled: searchParams.module === 'COLLECTION' && searchs }
  );

  return searchParams.module === 'CINVOICE'
    ? jobListByCriteriaClientInvoice
    : searchParams.module === 'PAYMENT'
    ? jobListByCriteriaPayement
    : searchParams.module === 'SINVOICE'
    ? jobListByCriteriaSupplierInvoice
    : searchParams.module === 'ACCOUNTING'
    ? jobListByCriteriaAccounting
    : searchParams.module === 'COLLECTION'
    ? jobListByCriteriaCollection
    : jobListByCriteriaClientInvoice;
};

export default ServicesFollowup;
